import React, { useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Link, Tooltip } from "@fluentui/react-components";
import { MailArrowUpRegular } from "@fluentui/react-icons";
import { format } from "date-fns";
import { QUERY_KEYS } from "library/shared";
import { formatDateTimeString } from "library/utils";
import {
  DEFAULT_SERVICE_PERSON_PHONE,
  GROUP_INVOICE_EMAIL_TEMPLATE,
} from "library/constants/emailTemplates";
import { SendEmailDialog } from "components/SendEmailDialog";
import { getInvoiceEmailAddressee } from "components/InvoicePayer";

// Same look as the document boxes of the quote tabs (MtplQuotesTab / CascoQuotesTab)
const FileInfoBox = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 1px solid ${({ theme }) => theme.semanticColors.bodyFrameDivider};
  border-radius: 8px;
  padding: 12px;
  background: ${({ theme }) => theme.semanticColors.bodyBackground};
`;

const FileInfoHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
`;

const DocumentLink = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  color: ${({ theme }) => theme.semanticColors.link};
  flex: 1;

  &:hover {
    color: ${({ theme }) => theme.semanticColors.linkHovered};
  }
`;

const SentInfo = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.semanticColors.bodySubtext};
`;

const OffersInfo = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.semanticColors.bodySubtext};
`;

const PdfIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="currentColor"
    style={{ marginRight: "8px", flexShrink: 0 }}
  >
    <path d="M4 4C4 2.89543 4.89543 2 6 2H10.5858C11.1162 2 11.6249 2.21071 12 2.58579L15.4142 6C15.7893 6.37507 16 6.88378 16 7.41421V16C16 17.1046 15.1046 18 14 18H6C4.89543 18 4 17.1046 4 16V4Z" />
    <path d="M11 2.5V6C11 6.55228 11.4477 7 12 7H15.5" fill="white" />
    <text x="10" y="14" fontSize="6" fill="white" textAnchor="middle" fontWeight="bold">
      PDF
    </text>
  </svg>
);

const DocxIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="#2B579A"
    style={{ marginRight: "8px", flexShrink: 0 }}
  >
    <path d="M4 4C4 2.89543 4.89543 2 6 2H10.5858C11.1162 2 11.6249 2.21071 12 2.58579L15.4142 6C15.7893 6.37507 16 6.88378 16 7.41421V16C16 17.1046 15.1046 18 14 18H6C4.89543 18 4 17.1046 4 16V4Z" />
    <path d="M11 2.5V6C11 6.55228 11.4477 7 12 7H15.5" fill="white" />
    <text x="10" y="14" fontSize="5" fill="white" textAnchor="middle" fontWeight="bold">
      DOCX
    </text>
  </svg>
);

interface Props {
  offerId: string;
  /** Full OfferListEntryDto of the offer (untyped, as in the quote tabs) */
  offerData: any;
  /** react-hook-form getValues of the proposal form (client, payer and vehicle data for the email template) */
  getValues: (name: string) => any;
  /** Logged-in user (store userInfoState) */
  userInfo: any;
  onOfferDataUpdate?: (updatedOffer: any) => void;
}

/**
 * Group invoice documents of an offer (Invoice.GroupInvoice): PDF and DOCX links plus "send by email".
 * Rendered inside the documents grid of the quote tabs; renders nothing when the offer is not part of a group invoice.
 * Sending goes through PATCH invoices/send/{offerId}/{groupInvoiceId}, which stamps all offers of the group.
 */
export const GroupInvoiceDocuments: React.FC<Props> = ({
  offerId,
  offerData,
  getValues,
  userInfo,
  onOfferDataUpdate,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);

  const groupInvoice = offerData?.Invoice?.GroupInvoice;
  const pdfPath: string | undefined = groupInvoice?.PathToInvoicePdf;
  const docxPath: string | undefined = groupInvoice?.PathToInvoiceDocx;
  const invoiceNumber: string = groupInvoice?.Request?.InvoiceNumber || "";
  const groupInvoiceId: string | undefined = groupInvoice?.Response?.InvoiceId;
  const offerCount: number = groupInvoice?.Response?.GroupOfferIds?.length || 0;

  if (!groupInvoice || !pdfPath || !offerId) {
    return null;
  }

  // refetch on click so a payment done meanwhile shows up (same as the single invoice link)
  const handlePdfClick = () => {
    queryClient.refetchQueries({ queryKey: [QUERY_KEYS.offer, offerId] });
  };

  // the group invoice is addressed to its payer (the clients of the grouped offers may differ)
  const isClientPerson = getValues("clientData.clientType") === "person";
  const addressee = getInvoiceEmailAddressee(groupInvoice.Request?.Payer, {
    isPerson: isClientPerson,
    name: isClientPerson
      ? `${getValues("clientData.clientFirstName") || ""} ${
          getValues("clientData.clientLastName") || ""
        }`.trim()
      : getValues("clientData.clientName") || "",
    email: getValues("clientData.clientEmail") || "",
  });

  return (
    <>
      <FileInfoBox>
        <FileInfoHeader>
          <DocumentLink href={pdfPath} target="_blank" onClick={handlePdfClick}>
            <PdfIcon />
            {t("qtg.proposal.documents.groupInvoicePdf", {
              number: invoiceNumber,
              defaultValue: "Group invoice {{number}} (PDF)",
            })}
          </DocumentLink>
          <Tooltip
            content={t(
              "qtg.proposal.buttons.sendEmailGroupInvoice.tooltip",
              "Send group invoice via email",
            )}
            relationship="label"
          >
            <Button
              appearance="subtle"
              size="medium"
              icon={<MailArrowUpRegular />}
              onClick={() => setIsEmailDialogOpen(true)}
              aria-label={t(
                "qtg.proposal.buttons.sendEmailGroupInvoice.tooltip",
                "Send group invoice via email",
              )}
            />
          </Tooltip>
        </FileInfoHeader>
        {offerCount > 0 && (
          <OffersInfo>
            {t("qtg.proposal.documents.groupInvoiceOffers", {
              count: offerCount,
              defaultValue: "{{count}} offer(s)",
            })}
          </OffersInfo>
        )}
        {groupInvoice.SentAt && groupInvoice.SentByUserFullName && (
          <SentInfo>
            {t("qtg.proposal.documents.groupInvoiceSentInfo", {
              sentByUserFullName: groupInvoice.SentByUserFullName,
              sentByUsername: groupInvoice.SentByUsername || "",
              sentTo: groupInvoice.SentTo || "",
              sentAt: format(new Date(groupInvoice.SentAt), "dd.MM.yyyy HH:mm"),
              defaultValue:
                "User {{sentByUserFullName}} ({{sentByUsername}}) emailed the group invoice to {{sentTo}} on {{sentAt}}.",
            })}
          </SentInfo>
        )}
      </FileInfoBox>

      {docxPath && (
        <FileInfoBox>
          <DocumentLink href={docxPath} target="_blank">
            <DocxIcon />
            {t("qtg.proposal.documents.groupInvoiceDocx", {
              number: invoiceNumber,
              defaultValue: "Group invoice {{number}} (DOCX)",
            })}
          </DocumentLink>
        </FileInfoBox>
      )}

      {groupInvoiceId && (
        <SendEmailDialog
          open={isEmailDialogOpen}
          onClose={() => setIsEmailDialogOpen(false)}
          offerId={offerId}
          invoiceId={groupInvoiceId}
          defaultAttachment={{
            name: t("qtg.proposal.documents.groupInvoicePdf", {
              number: invoiceNumber,
              defaultValue: "Group invoice {{number}} (PDF)",
            }),
            url: pdfPath,
          }}
          template={GROUP_INVOICE_EMAIL_TEMPLATE}
          templateData={{
            Prefix: addressee.Prefix,
            ClientName: addressee.ClientName,
            MarkModel: `${getValues("vehicleData.vehicleMake") || ""} ${
              getValues("vehicleData.vehicleModel") || ""
            }`.trim(),
            PlateNo: getValues("vehicleData.licensePlateNumber") || "",
            PolicyEndDate:
              formatDateTimeString(
                offerData?.Offer?.Request?.PreviousInsuranceEndDate,
              ) || "",
            ServicePersonName: userInfo?.userFullName || userInfo?.name || "XXX",
            ServicePersonPhone:
              userInfo?.userPhoneNumber || DEFAULT_SERVICE_PERSON_PHONE,
            ServicePersonEMail: userInfo?.userEmail || userInfo?.email || "xxx@greco.eu",
          }}
          defaultFromName={userInfo?.userFullName || userInfo?.name || ""}
          defaultFromEmail={userInfo?.userEmail || userInfo?.email || ""}
          defaultToEmail={addressee.email}
          onEmailSent={(updatedOffer) => {
            if (onOfferDataUpdate) {
              onOfferDataUpdate(updatedOffer);
            }
          }}
        />
      )}
    </>
  );
};
